import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { handleApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatCurrency, formatNumber, formatDateTime, statusBadgeClass, statusLabel } from '../utils/formatters';
import {
  TrendingUp, ShoppingCart, Package, AlertTriangle, CreditCard,
  Receipt, FileText, Clock, Bell,
  Users, Truck, RefreshCw, PackageCheck, BarChart3, DollarSign,
  Inbox, ChevronRight
} from 'lucide-react';
import StatCard, { StatCardSkeleton } from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';

import { Line, Bar } from 'react-chartjs-2';

const BRAND_SOFT = '#E6FF94';

function NotificationsPanel({ notifications }) {
  return (
    <div className="card h-full flex flex-col shadow-card">
      <div className="card-header flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-medium text-surface-900 dark:text-white">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-primary-900 dark:bg-primary-950 dark:text-primary-300">
            <Bell size={14} />
          </span>
          Notifikasi
        </h3>
        <Link to="/notifications" className="flex items-center gap-0.5 text-xs font-medium text-primary-900 transition-colors hover:text-primary-950 dark:text-primary-400 dark:hover:text-primary-300">
          Lihat Semua
          <ChevronRight size={12} className="mt-px" />
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto">
        {(!notifications || notifications.length === 0) ? (
          <div className="flex flex-col items-center justify-center h-full py-10 text-center">
            <div className="w-12 h-12 rounded-2xl bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-3">
              <Inbox size={20} className="text-surface-400 dark:text-surface-500" />
            </div>
            <p className="text-sm text-surface-400 dark:text-surface-500">Tidak ada notifikasi</p>
          </div>
        ) : (
          <div className="divide-y divide-surface-100 dark:divide-surface-800">
            {notifications.slice(0, 5).map((n) => (
              <div key={n.id} className="px-5 py-3.5 hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-2 flex-shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-sm text-surface-700 dark:text-surface-200 line-clamp-2">{n.message}</p>
                  <p className="text-xs text-surface-400 dark:text-surface-500 mt-0.5">{formatDateTime(n.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function chartOptions(theme = 'light') {
  const gridColor = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15, 23, 42, 0.06)';
  const tickColor = theme === 'dark' ? '#94A3B8' : '#566275';
  const tooltipBg = theme === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.96)';
  const tooltipText = theme === 'dark' ? '#E2E8F0' : '#0F172A';
  const tooltipBorder = theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(148, 163, 184, 0.3)';
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    // Keep the final point and its label inside the canvas.
    layout: { padding: { top: 4, right: 8 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: tooltipText,
        bodyColor: tooltipText,
        borderColor: tooltipBorder,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 12,
        displayColors: false,
        callbacks: {
          label: (ctx) => ` ${formatCurrency(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tickColor, font: { size: 11 } }, border: { display: false } },
      y: { beginAtZero: true, grid: { color: gridColor }, border: { display: false }, ticks: { color: tickColor, font: { size: 11 }, maxTicksLimit: 6, callback: (v) => formatNumber(v) } },
    },
  };
}

function barOptions(theme = 'light') {
  const gridColor = theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(15, 23, 42, 0.06)';
  const tickColor = theme === 'dark' ? '#94A3B8' : '#566275';
  const tooltipBg = theme === 'dark' ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.96)';
  const tooltipText = theme === 'dark' ? '#E2E8F0' : '#0F172A';
  const tooltipBorder = theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(148, 163, 184, 0.3)';
  return {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: tooltipText,
        bodyColor: tooltipText,
        borderColor: tooltipBorder,
        borderWidth: 1,
        padding: 12,
        cornerRadius: 12,
        displayColors: false,
        callbacks: {
          label: (ctx) => ` ${formatNumber(ctx.parsed.x)} unit`,
        },
      },
    },
    scales: {
      x: { beginAtZero: true, grid: { color: gridColor }, border: { display: false }, ticks: { color: tickColor, font: { size: 11 }, maxTicksLimit: 5, callback: (v) => formatNumber(v) } },
      y: { grid: { display: false }, border: { display: false }, ticks: { color: tickColor, font: { size: 11 } } },
    },
  };
}

export default function DashboardPage() {
  const { user, hasRole } = useAuth();
  const { isDark } = useTheme();
  const chartBrand = isDark ? '#D5F66F' : '#7E9425';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard');
      setData(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const salesTrendData = useMemo(() => {
    const points = data?.salesTrend || [];
    return {
      labels: points.map(d => d.label),
      datasets: [{
        label: 'Penjualan',
        data: points.map(d => d.value),
        borderColor: chartBrand,
        backgroundColor: (context) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;
          if (!chartArea) return 'rgba(194, 224, 80, 0.18)';
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(194, 224, 80, 0.38)');
          gradient.addColorStop(1, 'rgba(194, 224, 80, 0)');
          return gradient;
        },
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        // A single point draws no line segment, so hide nothing in that case.
        pointRadius: points.length === 1 ? 4 : 0,
        pointBackgroundColor: chartBrand,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: chartBrand,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
      }],
    };
  }, [data?.salesTrend, chartBrand]);

  const topProductsData = useMemo(() => ({
    labels: data?.topProducts?.map(d => d.name) || [],
    datasets: [{
      label: 'Qty Terjual',
      data: data?.topProducts?.map(d => d.quantity) || [],
      backgroundColor: BRAND_SOFT,
      hoverBackgroundColor: '#C2E050',
      borderColor: chartBrand,
      borderWidth: 1,
      borderRadius: 6,
      borderSkipped: false,
      barThickness: 10,
      maxBarThickness: 12,
    }],
  }), [data?.topProducts, chartBrand]);

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" aria-busy="true" aria-label="Memuat dashboard">
        <div>
          <div className="skeleton h-7 w-48 rounded-lg" />
          <div className="skeleton h-4 w-72 mt-2 rounded-lg" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(8)].map((_, i) => <StatCardSkeleton key={i} />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-5"><div className="skeleton h-64 w-full rounded-lg" /></div>
          <div className="card p-5"><div className="skeleton h-64 w-full rounded-lg" /></div>
        </div>
        <div className="card h-64"><div className="skeleton h-full w-full rounded-lg" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/60 px-6 py-7 shadow-card backdrop-blur-xl dark:border-white/10 dark:bg-surface-800/60 sm:px-8">
        <div className="absolute inset-y-0 right-0 hidden w-1/2 bg-dotted opacity-60 dark:opacity-40 sm:block" aria-hidden="true" />
        <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-primary-600/40 blur-3xl dark:bg-primary-600/10" aria-hidden="true" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <span className="chip mb-3">
              <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              {data?.period || 'Periode berjalan'}
            </span>
            <h1 className="text-2xl leading-tight tracking-[-0.4px] text-surface-900 dark:text-white sm:text-[1.75rem]">
              Selamat datang, <span className="text-primary-900 dark:text-primary-400">{user?.fullName?.split(' ')[0] || user?.name || 'Tim'}</span>
            </h1>
            <p className="mt-1.5 text-sm text-surface-500 dark:text-surface-400">
              Ringkasan operasi dan keuangan Anda hari ini.
              <span className="mx-2 text-surface-300 dark:text-surface-600">&bull;</span>
              <span className="font-medium text-surface-700 dark:text-surface-200">{user?.role?.displayName}</span>
            </p>
          </div>
          <button onClick={fetchDashboard} className="btn-outline btn-sm w-fit flex-shrink-0" aria-label="Perbarui data dashboard">
            <RefreshCw size={14} />
            Perbarui
          </button>
        </div>
      </div>

      {/* Owner / Finance Stats */}
      {hasRole('owner', 'finance') && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Penjualan Hari Ini" value={formatCurrency(data?.todaySales)} icon={ShoppingCart} color="indigo" trend={data?.salesTrendPct > 0 ? 'up' : 'down'} trendValue={Math.abs(data?.salesTrendPct || 0)} subtitle="Periode berjalan" onClick={() => navigate('/sales/history')} />
            <StatCard title="Pembelian Bulan Ini" value={formatCurrency(data?.totalPurchases)} icon={Package} color="teal" trend={data?.purchaseTrendPct > 0 ? 'up' : 'down'} trendValue={Math.abs(data?.purchaseTrendPct || 0)} subtitle={data?.period} onClick={() => navigate('/purchasing/orders')} />
            <StatCard title="Laba Bersih" value={formatCurrency(data?.netProfit)} icon={TrendingUp} color="green" trendValue={Math.abs(data?.netProfitPct || 0)} subtitle="Perkiraan" onClick={() => navigate('/reports/profit-loss')} />
            <StatCard title="Nilai Persediaan" value={formatCurrency(data?.inventoryValue)} icon={PackageCheck} color="blue" subtitle="Total stok aktif" onClick={() => navigate('/inventory/stock')} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard title="Piutang" value={formatCurrency(data?.totalReceivables)} icon={CreditCard} color="amber" subtitle="Belum dibayar" onClick={() => navigate('/finance/receivables')} />
            <StatCard title="Utang" value={formatCurrency(data?.totalPayables)} icon={Receipt} color="red" subtitle="Belum dibayar" onClick={() => navigate('/finance/payables')} />
            <StatCard title="Penjualan Bulan Ini" value={formatCurrency(data?.totalSales)} icon={BarChart3} color="purple" subtitle={data?.period} onClick={() => navigate('/sales/history')} />
            <StatCard title="Stok Menipis" value={formatNumber(data?.lowStockCount)} icon={AlertTriangle} color="orange" subtitle="Perlu dipesan ulang" onClick={() => navigate('/inventory/stock')} />
          </div>
        </>
      )}

      {/* Non-owner stats */}
      {hasRole('inventory_admin') && !hasRole('owner', 'finance') && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total SKU" value={formatNumber(data?.totalProducts)} icon={Package} color="blue" />
          <StatCard title="Stok Menipis" value={formatNumber(data?.lowStockCount)} icon={AlertTriangle} color="orange" />
          <StatCard title="Nilai Persediaan" value={formatCurrency(data?.inventoryValue)} icon={DollarSign} color="green" />
          <StatCard title="Pergerakan Hari Ini" value={formatNumber(data?.todayMovements)} icon={RefreshCw} color="purple" />
        </div>
      )}
      {hasRole('sales') && !hasRole('owner', 'finance') && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Penjualan Hari Ini" value={formatCurrency(data?.todaySales)} icon={ShoppingCart} color="blue" />
          <StatCard title="Invoice Belum Dibayar" value={formatNumber(data?.unpaidInvoices)} icon={FileText} color="red" />
          <StatCard title="Total Pelanggan" value={formatNumber(data?.totalCustomers)} icon={Users} color="green" />
          <StatCard title="Produk Terlaris" value={data?.topProduct?.name || '-'} icon={Package} color="purple" />
        </div>
      )}
      {hasRole('purchasing') && !hasRole('owner', 'finance') && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard title="PO Pending" value={formatNumber(data?.pendingPOs)} icon={FileText} color="amber" />
          <StatCard title="Penerimaan" value={formatNumber(data?.recentReceipts)} icon={Truck} color="blue" />
          <StatCard title="Pembayaran Due" value={formatCurrency(data?.supplierPaymentsDue)} icon={CreditCard} color="red" />
          <StatCard title="Total Supplier" value={formatNumber(data?.totalSuppliers)} icon={Users} color="green" />
        </div>
      )}

      {/* Charts */}
      {hasRole('owner', 'finance') && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card shadow-card">
            <div className="card-header flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-medium text-surface-900 dark:text-white">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-primary-900 dark:bg-primary-950 dark:text-primary-300">
                  <TrendingUp size={14} />
                </span>
                Tren Penjualan
              </h3>
              <span className="chip bg-surface-50 text-[10px] dark:bg-surface-950">6 bulan terakhir</span>
            </div>
            <div className="card-body h-72">
              <Line data={salesTrendData} options={chartOptions(isDark)} />
            </div>
          </div>
          <div className="card shadow-card">
            <div className="card-header flex items-center justify-between">
              <h3 className="font-semibold text-surface-900 dark:text-white text-sm flex items-center gap-2">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-primary-900 dark:bg-primary-950 dark:text-primary-300">
                  <PackageCheck size={14} />
                </span>
                Produk Terlaris
              </h3>
              <span className="chip bg-surface-50 text-[10px] dark:bg-surface-950">{data?.topProducts?.length || 0} produk</span>
            </div>
            <div className="card-body h-72">
              {data?.topProducts?.length ? (
                <Bar data={topProductsData} options={barOptions(isDark)} />
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-12 h-12 rounded-2xl bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-3">
                    <Package size={20} className="text-surface-400 dark:text-surface-500" />
                  </div>
                  <p className="text-sm text-surface-400 dark:text-surface-500">Belum ada produk terjual</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recent Transactions & Notifications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card shadow-card">
          <div className="card-header flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-medium text-surface-900 dark:text-white">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 text-primary-900 dark:bg-primary-950 dark:text-primary-300">
                <Clock size={14} />
              </span>
              Transaksi Terbaru
            </h3>
            {(data?.recentTransactions?.length > 0) && (
              <span className="chip bg-surface-50 text-[10px] dark:bg-surface-950">{data.recentTransactions.length} transaksi</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>No. Transaksi</th>
                  <th>Tanggal</th>
                  <th>Deskripsi</th>
                  <th className="text-right">Jumlah</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(!data?.recentTransactions || data.recentTransactions.length === 0) ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                        <div className="w-12 h-12 rounded-2xl bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-3">
                          <Inbox size={20} className="text-surface-400 dark:text-surface-500" />
                        </div>
                        <p className="text-sm text-surface-400 dark:text-surface-500">Belum ada transaksi</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  data.recentTransactions.slice(0, 5).map((tx, idx) => (
                    <tr key={tx.id || idx}>
                      <td className="font-medium text-surface-900 dark:text-white">{tx.number}</td>
                      <td className="whitespace-nowrap">{formatDateTime(tx.date)}</td>
                      <td className="max-w-[200px] truncate">{tx.description}</td>
                      <td className="text-right font-medium tabular-nums">{formatCurrency(tx.amount)}</td>
                      <td>
                        <StatusBadge status={statusBadgeClass(tx.status).replace('badge-', '')} customLabel={statusLabel(tx.status)} dot />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <NotificationsPanel notifications={data?.notifications} />
      </div>
    </div>
  );
}